import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Route, UserRoundPen, UserCheck2, Ticket, TicketCheck, Award, Loader2 } from 'lucide-react';
import { GiBoxUnpacking } from "react-icons/gi";
import { FloatingButton } from "react-draggable-float-btn";
import { dashboardService , type DashboardData, type SkillPathway } from '@/services/dashboardService';
import { StatCard } from './StatCard';
import './HeroSection.css';

interface HeroSectionProps {
  userData?: DashboardData | null;
  selectedTree?: SkillPathway|null;
  treeLoading?: boolean;
}

export const HeroSection: React.FC<HeroSectionProps> = ({ 
  userData, 
  selectedTree, 
  treeLoading = false 
}) => {
  const [isLoading, setIsLoading] = useState(!userData);
  
  // Update loading state when userData changes
  useEffect(() => {
    setIsLoading(!userData);
  }, [userData]);
  
  // Safe data extraction with fallbacks
  const userName = userData?.user?.name || 
                   `${userData?.user?.first_name || ''} ${userData?.user?.last_name || ''}`.trim() || 
                   'Guest';
  
  const enrolledCount = userData?.enrolledPathways || 0;
  const completedCount = userData?.statistics?.completed_skillstrees || 0;
  const pathwayCount = userData?.statistics?.total_skillstrees || 0;
  const achievementCount = userData?.statistics?.total_badges || 0;
  const coachingUsed = userData?.statistics?.attended_coaching || 0;
  const networkingUsed = userData?.statistics?.attended_networking || 0;
  const coachingCount = userData?.statistics?.remaining_coaching || 0;
  const networkingCount = userData?.statistics?.remaining_networking || 0;
  
  const userPathwayCount = userData?.pathwayCount as number || 0;
  const back = userPathwayCount > 0 ? '' : ' back';
  const welcome = `Welcome${back}, ${userName}.`;
  
  const userRole = userData?.user?.role;
  const roleId = userRole?.id || 0;
  const isPlus = userData?.isPlus || false;
  let planUntil = userData?.planValidUntil;
  
  // Determine plan status with safe checks
  let planEnded = true;
  if (planUntil) {
    try {
      planEnded = new Date(planUntil) < new Date();
    } catch (e) {
      planEnded = true;
    }
  }
  
  let plan = 'Subscription';
  let valid = 'not found';
  let bgColor = 'bg-gray-500';
  let bgAnim = 'bg-gray-400';
  let bgPing = 'bg-gray-500';

  if (planUntil && roleId > 1 && roleId < 5) {
    plan = isPlus ? 'Plan' : 'Free trial';
    if (planEnded) {
      valid = 'ended on';
    } else if (isPlus) {
      valid = 'valid until';
      bgColor = 'bg-orange-500';
      bgAnim = 'bg-orange-400';
      bgPing = 'bg-orange-500';
    } else {
      valid = 'ends on';
      bgColor = 'bg-amber-500';
      bgAnim = 'bg-amber-400';
      bgPing = 'bg-amber-500';
    }
  } else {
    planUntil = null;
  }
  
  const navigate = useNavigate();

  let image: string|null = null;
  const images = {
    "Big Data": '/badge/big-data/is1-bd.png',
    "Cloud Computing": '/badge/cloud-computing/is3-cc.png',
    "Cybersecurity": '/badge/cybersecurity/is2-cs.png',
    "Digital Product Design": '/badge/digital-product-design/is4-pd.png',
    "Digital Marketing": '/badge/product-marketing/is5-dm.png',    
    'big-data': '/big_data.jpg',
    'digital-marketing': '/digital_marketing.jpg',
    'cloud-computing': '/cloud_computing.jpg',
    'cyber-security': '/cyber_security.jpg',
    'cybersecurity': '/cyber_security.jpg',
    'digital-product-design': '/digital_design.jpg',
  };

  const checkImage = (url: string|null) => !url ? null : (url.startsWith("https://placehold.co") ? null : url);
  
  if (selectedTree) {
    image = checkImage(selectedTree?.image_url as string|null) || 
            images[selectedTree?.core_category_name as keyof typeof images] || 
            images[selectedTree?.core_category_slug as keyof typeof images] || 
            null;
  }

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        day: '2-digit', 
        month: 'short', 
        year: '2-digit' 
      });
    } catch (e) {
      return 'Invalid date';
    }
  };

  // State for upcoming microskill
  const [upcomingMicroskill, setUpcomingMicroskill] = useState<{
    micro_skill_version_id: number;
    micro_skill_id: number;
    title: string;
    duration: number;
    image_url: string;
    skillstree_id: number;
    core_category_id: number;
  } | null>(null);
  const [loadingUpcoming, setLoadingUpcoming] = useState(false);

  // Format duration in minutes to "X min" or "X hr Y min"
  const formatDuration = (seconds: number): string => {
    if (!seconds) return '0 min';
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours > 0 && mins > 0) {
      return `${hours}hr ${mins}min`;
    } else if (hours > 0) {
      return `${hours}hr`;
    } else {
      return `${mins} min`;
    }
  };

  // Fetch and find upcoming microskill - only when userData is available
  useEffect(() => {
    if (!userData) return; // Don't fetch if userData is not available
    
    const fetchUpcomingMicroskill = async () => {
      try {
        setLoadingUpcoming(true);
        const response = await dashboardService.getSkillstrees();
        const skillstrees = (response?.data as any)?.data || [];

        // Filter enrolled skillstrees
        const enrolledSkillstrees = skillstrees.filter((st: any) => st.is_enrolled === true);

        // Find the next upcoming microskill
        // Priority: in_progress > not_started
        let nextMicroskill: any = null;
        let inProgressMicroskill: any = null;
        
        for (const skillstree of enrolledSkillstrees) {
          if (!skillstree.versions || skillstree.versions.length === 0) continue;
          
          const version = skillstree.versions[0]; // Use first version
          if (!version.micro_skills || version.micro_skills.length === 0) continue;

          // First, try to find in_progress microskill
          if (!inProgressMicroskill) {
            const inProgress = version.micro_skills.find((ms: any) => 
              ms.learning_status === 'in_progress' && ms.can_access === true
            );
            
            if (inProgress) {
              inProgressMicroskill = {
                ...inProgress,
                skillstree_id: skillstree.skillstree_id,
                core_category_id: skillstree.core_category_id,
              };
            }
          }

          // If no in_progress found, look for not_started
          if (!nextMicroskill && !inProgressMicroskill) {
            const notStarted = version.micro_skills.find((ms: any) => 
              ms.learning_status === 'not_started' && ms.can_access === true
            );

            if (notStarted) {
              nextMicroskill = {
                ...notStarted,
                skillstree_id: skillstree.skillstree_id,
                core_category_id: skillstree.core_category_id,
              };
            }
          }
        }

        // Prefer in_progress over not_started
        if (inProgressMicroskill) {
          nextMicroskill = inProgressMicroskill;
        }

        // If we found a microskill, enrich it with the proper thumbnail from the detailed endpoint
        if (nextMicroskill) {
          try {
            const detailRes = await dashboardService.getSkillstree(String(nextMicroskill.skillstree_id));
            const detailData = (detailRes?.data as any)?.data;
            const detailVersion = detailData?.versions?.[0];
            const detailMicroskill = detailVersion?.micro_skills?.find(
              (ms: any) =>
                ms.micro_skill_id === nextMicroskill.micro_skill_id ||
                ms.micro_skill_version_id === nextMicroskill.micro_skill_version_id
            );

            // Find a video thumbnail with learning_status in_progress, otherwise fall back
            const targetVideo =
              detailMicroskill?.videos?.find((v: any) => v.learning_status === 'in_progress') ||
              detailMicroskill?.videos?.find((v: any) => v.learning_status === 'not_started') ||
              detailMicroskill?.videos?.[0];

            const thumbnailUrl = targetVideo?.thumbnail?.url || detailMicroskill?.image_url || nextMicroskill.image_url;

            setUpcomingMicroskill({
              ...nextMicroskill,
              image_url: thumbnailUrl,
            });
          } catch (err) {
            console.error('Error fetching microskill detail for thumbnail:', err);
            setUpcomingMicroskill(nextMicroskill);
          }
        } else {
          setUpcomingMicroskill(null);
        }
      } catch (error) {
        console.error('Error fetching upcoming microskill:', error);
      } finally {
        setLoadingUpcoming(false);
      }
    };

    fetchUpcomingMicroskill();
  }, [userData, treeLoading]); // Add userData to dependencies

  const handlePlayNext = () => {
    if (upcomingMicroskill) {
      navigate(`/skillstree/${upcomingMicroskill.core_category_id}/${upcomingMicroskill.skillstree_id}/${upcomingMicroskill.micro_skill_version_id}`);
    }
  };

  const getFullImageUrl = (url: string | null) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    if (url.startsWith('https://placehold.co')) return null;
    return url;
  };
  
  // Show loading state if data is not available
  if (isLoading) {
    return (
      <section className="relative pt-10 pb-6">
        <div className="mx-auto px-4">
          <div className="grid grid-cols-5 grid-col-reverse items-center justify-center content-center gap-6 lg:gap-1">
            {/* Loading skeleton for the entire section */}
            <div className="col-span-5 lg:col-span-1 order-last lg:order-first w-56 h-56 lg:h-52 relative mx-auto lg:mx-0">
              <div className="w-56 h-56 lg:h-52 absolute bg-zinc-800/10 rounded-2xl border border-dashed border-white animate-pulse"></div>
            </div>
            
            <div className="col-span-5 lg:col-span-3">
              <div className="text-center mb-12">
                <div className="animate-pulse bg-white/70 rounded h-10 w-80 mx-auto mb-4"></div>
              </div>
              <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-0 max-w-4xl mx-auto md:divide-x md:divide-gray-300/80">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="p-4">
                    <div className="animate-pulse bg-white/70 rounded h-6 w-24 mb-2 mx-auto"></div>
                    <div className="animate-pulse bg-white/70 rounded h-8 w-12 mx-auto"></div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="h-full order-first lg:order-last col-span-5 lg:col-span-1 flex flex-col items-center content-center lg:items-start lg:content-start place-content-stretch gap-4">
              <div className="animate-pulse bg-white/70 rounded h-10 w-72"></div>
              <div className="animate-pulse bg-white/70 rounded h-[200px] w-72"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }
  
  return (
    <section className="relative pt-10 pb-6">
      <div className="mx-auto px-4">
        <div className="grid grid-cols-5 grid-col-reverse items-center justify-center content-center gap-6 lg:gap-1">
          <div className="col-span-5 lg:col-span-1 order-last lg:order-first w-56 h-56 lg:h-52 relative mx-auto lg:mx-0">
            <div className="w-56 h-56 lg:h-52 absolute bg-zinc-800/10 rounded-2xl border border-dashed border-white" />
            {treeLoading ? (
              // Loading state for tree image
              <div className="w-full h-full flex items-center justify-center animate-pulse">
                <div className="w-48 h-48 bg-white/70 rounded-2xl"></div>
              </div>
            ) : selectedTree ? (
              <div className="w-full h-full flex items-center justify-center">
                <img 
                  className="flex h-auto max-w-52 min-w-48 object-center" 
                  src={image || '/pages/achievements/Digital-Badges.png'} 
                  alt={selectedTree?.name || selectedTree?.core_category_name || 'Skillstree' } 
                  onError={(e) => e.currentTarget.src = '/pages/achievements/Digital-Badges.png'}
                />
              </div>
            ) : (
              <>
                <div className="w-24 h-24 left-[67px] top-[25px] absolute rounded-full outline outline-2 outline-offset-[-2px] outline-gray-500 inline-flex justify-center items-center">
                  <Award size={50} className="text-gray-500"/>
                </div>
                <div className="w-48 h-12 left-[15.25px] top-[137px] absolute">
                  <div className="w-36 h-7 left-[25.75px] top-0 absolute bg-slate-800/50 rounded-full">
                    <div className="left-[14px] top-[4.50px] absolute text-center justify-start text-gray-400 text-sm font-normal leading-5">
                      No job stamp yet
                    </div>
                  </div>
                  <div className="w-52 h-4 left-0 top-[36px] absolute">
                    <div className="left-[1px] top-[1px] absolute text-center justify-start text-gray-500 text-xs font-normal leading-4">
                      Select Skillstree pathway to earn 
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          
          <div className="col-span-5 lg:col-span-3">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold mb-4">
                {treeLoading ? (
                  <div className="animate-pulse bg-white/70 rounded h-10 w-80 mx-auto"></div>
                ) : (
                  welcome
                )}
              </h1>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-0 max-w-4xl mx-auto md:divide-x md:divide-gray-300/80">
              <StatCard 
                title="Skillstree Pathway"
                tooltip={`${pathwayCount} skillstree pathway(s) enrolled`}
                value={pathwayCount || 0}
                icon={Route}
                loading={treeLoading}
              />
              <StatCard 
                title="Online Coaching" 
                tooltip={`${coachingCount} remain and ${coachingUsed} used`}
                value={coachingCount || 0}
                icon={UserRoundPen}
                value2={coachingUsed || 0}
                icon2={UserCheck2}
                loading={treeLoading}
              />
              <StatCard 
                title="Professional Networking"
                tooltip={`${networkingCount} remain and ${networkingUsed} used`}
                value={networkingCount || 0}
                icon={Ticket}
                value2={networkingUsed || 0}
                icon2={TicketCheck}
                loading={treeLoading}
              />
              <StatCard 
                goto="/achievements"
                title="Achievements" 
                tooltip={`${achievementCount} achievement(s)`}
                value={achievementCount || 0}
                icon={Award}
                loading={treeLoading}
              />
            </div>
          </div>
          
          <div className="h-full order-first lg:order-last col-span-5 lg:col-span-1 flex flex-col items-center content-center lg:items-start lg:content-start place-content-stretch gap-4">
            {/* Plan Button */}
            {treeLoading ? (
              <div className="animate-pulse bg-white/70 rounded h-10 w-72"></div>
            ) : (
              <button
                onClick={() => navigate('/profile?tab=subscription')}
                className={`${bgColor} shadow-lg hover:shadow-xl transition-shadow duration-200 rounded-sm outline outline-1 outline-offset-[-1px] outline-neutral-200 inline-flex justify-center items-center px-4 py-2 relative`}
                style={{ width: '265px', height: '40px' }}
              >
                <div className="text-center justify-center px-2 py-1 w-full">
                  <span className="text-white text-sm font-medium underline leading-5">{plan}</span>
                  <span className="text-white text-sm font-medium leading-5"> {valid} </span>
                  {planUntil && <span className="text-white text-sm font-medium leading-5">{formatDate(planUntil)}</span>}
                  <span className="absolute top-0 right-0 -mt-1 -mr-1 flex size-3">
                    <span className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 ${bgAnim}`}></span>
                    <span className={`relative inline-flex size-3 rounded-full outline-1 outline-offset-[-1px] outline-neutral-200 border border-neutral-200 ${bgPing}`}></span>
                  </span>
                </div>
              </button>
            )}

            {/* Up Next Section */}
            {upcomingMicroskill && !loadingUpcoming && (
              <div 
                className="bg-white rounded-[18px] shadow-lg overflow-hidden cursor-pointer hover:shadow-xl transition-shadow opacity-100"
                style={{ width: '265px', height: '200px' }}
                onClick={handlePlayNext}
              >
                {/* Thumbnail Section with Play Button Overlay */}
                <div className="relative h-[120px] overflow-hidden bg-white">
                  {getFullImageUrl(upcomingMicroskill.image_url) ? (
                    <>
                      <img 
                        src={getFullImageUrl(upcomingMicroskill.image_url) || ''} 
                        alt={upcomingMicroskill.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-500 to-blue-700">
                    </div>
                  )}
                </div>
                
                {/* Content Section */}
                <div className="p-3 h-[66px] flex flex-col justify-between">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-black text-xs font-bold mb-1">Up Next</div>
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-black text-sm font-medium text-left flex-1 line-clamp-2">{upcomingMicroskill.title}</h3>
                        <span className="text-black text-sm whitespace-nowrap flex-shrink-0">{formatDuration(upcomingMicroskill.duration)}</span>
                      </div>
                    </div>
                    <button 
                      className="w-10 h-10 rounded-full flex items-center justify-center transition-colors flex-shrink-0 hover:opacity-80"
                      style={{ backgroundColor: 'transparent', border: 'none', padding: 0 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePlayNext();
                      }}
                      aria-label="Play next microskill"
                    >
                      <svg width="29" height="29" viewBox="0 0 29 29" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-16 h-16">
                        <path fillRule="evenodd" clipRule="evenodd" d="M0 14.5C0 10.6544 1.52767 6.96623 4.24695 4.24695C6.96623 1.52767 10.6544 0 14.5 0C18.3456 0 22.0338 1.52767 24.753 4.24695C27.4723 6.96623 29 10.6544 29 14.5C29 18.3456 27.4723 22.0338 24.753 24.753C22.0338 27.4723 18.3456 29 14.5 29C10.6544 29 6.96623 27.4723 4.24695 24.753C1.52767 22.0338 0 18.3456 0 14.5ZM19.3333 9.66667H21.2667V19.3333H19.3333V9.66667ZM9.17947 9.79427C9.03248 9.7103 8.86601 9.66641 8.69673 9.66698C8.52745 9.66756 8.36129 9.71257 8.21487 9.79753C8.06845 9.88248 7.94691 10.0044 7.8624 10.1511C7.77789 10.2978 7.73339 10.4641 7.73333 10.6333V18.3667C7.73339 18.5359 7.77789 18.7022 7.8624 18.8489C7.94691 18.9956 8.06845 19.1175 8.21487 19.2025C8.36129 19.2874 8.52745 19.3324 8.69673 19.333C8.86601 19.3336 9.03248 19.2897 9.17947 19.2057L15.9461 15.3391C16.094 15.2545 16.2169 15.1323 16.3023 14.985C16.3878 14.8376 16.4328 14.6703 16.4328 14.5C16.4328 14.3297 16.3878 14.1624 16.3023 14.015C16.2169 13.8677 16.094 13.7455 15.9461 13.6609L9.17947 9.79427Z" fill="black" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};